import { Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { PaginationComponent } from '../../elements/pagination/pagination.component';
import { MatSortModule, Sort } from '@angular/material/sort';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { USER_PERMISSIONS } from '../../app.constants';
import { Select2Data } from 'ng-select2-component';
import { NgSelectModule } from '@ng-select/ng-select';
import { CommonModule } from '@angular/common';
import { FormsModule, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
declare const bootstrap: any;

@Component({
  selector: 'app-enquiries-list',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, BreadcrumbComponent, PaginationComponent, MatSortModule, NgSelectModule, RouterLink],
  templateUrl: './enquiries-list.component.html',
  styleUrl: './enquiries-list.component.css'
})
export class EnquiriesListComponent implements OnInit, OnDestroy {
  @Input() propertyId: string | null = null;
  @Input() hidePropertyColumn: boolean = false;

  breadcrumbList = {
    breadcrumb_path: 'Dashboard',
    currentURL: 'Enquiries',
    breadcrumb_url: '/admin'
  };

  private httpService = inject(HttpServiceService);
  private authService = inject(LocalStorageService);
  loadingService = inject(LoadingService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private alertService = inject(NgxToastrService);

  page: number = 1;
  totalRows: number = 50;
  totalPage: number = 0;
  pageCount: number = 1;
  allData: any = [];
  orderData: any = [];
  sortBy: string = 'created_at';
  orderBy: string = 'DESC';

  /** filter criteria */
  /** currently selected platform; null shows placeholder */
  filterPlatform: string | null = null;
  /** static list of platforms for dropdown */
  platformItems: Select2Data = [
    { value: 'Magicbricks', label: 'Magicbricks' },
    { value: '99acres', label: '99acres' },
    { value: 'Housing', label: 'Housing' },
    { value: 'Direct', label: 'Direct' },
  ];
  /** currently selected property id; null shows placeholder */
  filterProperty: string | null = null;
  /** currently selected status; null shows placeholder */
  filterStatus: string | null = null;
  /** free-text search term */
  filterSearch: string = '';
  /** raw list of properties for lazy loading */
  propertiesList: any[] = [];
  /** dropdown items for Select2 */
  propertiesItems: Select2Data = [];
  /** Subject for property search/typeahead */
  searchProperty$ = new Subject<string>();
  private propertySearchSub?: Subscription;
  /** lazy load pagination for properties */
  private propertyPage = 1;
  private propertyPageSize = 20;
  private hasMoreProperties = true;

  private getAllEnquiriesSubscription?: Subscription;

  // Update modal
  @ViewChild('updateModal') updateModal!: ElementRef;
  private modalInstance: any = null;
  updateForm!: FormGroup;
  selectedEnquiryId: string = '';

  // Status options
  statusOptions = [
    { value: 'HOT', label: 'Hot' },
    { value: 'WARM', label: 'Warm' },
    { value: 'COLD', label: 'Cold' },
    { value: 'CONVERTED', label: 'Converted' },
    { value: 'REJECT', label: 'Reject' },

  ];

  // Add Enquiry Modal
  @ViewChild('addEnquiryModal') addEnquiryModal!: ElementRef;
  private addModalInstance: any = null;
  addEnquiryForm!: FormGroup;
  showAddModal: boolean = false;

  // Property dropdown for add form
  addFormPropertiesList: any[] = [];
  addFormPropertiesItems: Select2Data = [];
  searchAddFormProperty$ = new Subject<string>();
  private addFormPropertySearchSub?: Subscription;
  private addFormPropertyPage = 1;
  private addFormPropertyPageSize = 20;
  private addFormHasMoreProperties = true;

  ngOnInit(): void {
    this.initUpdateForm();
    this.initAddEnquiryForm();
    this.setupPropertySearch();
    this.setupAddFormPropertySearch();
    // Coming back from a view/edit page: the filters were carried out in the
    // URL, so re-apply them before the first fetch.
    if (!this.propertyId) {
      this.restoreFilters(this.route.snapshot.queryParams);
    }
    this.getAllEnquiries();
  }

  /**
   * Filter state to carry into a detail route so the Back/Cancel button can
   * return to the same filtered page.
   */
  private currentFilterState(): Params {
    const state: Params = { page: this.page };
    const platform = this.unwrapFilter(this.filterPlatform);
    if (platform) state['platform'] = platform;
    const property = this.unwrapFilter(this.filterProperty);
    if (property) {
      state['property_id'] = property;
      state['property_name'] = this.labelOfFilter(this.filterProperty);
    }
    if (this.filterStatus) state['status'] = this.filterStatus;
    if (this.filterSearch) state['search'] = this.filterSearch;
    state['sortBy'] = this.sortBy;
    state['orderBy'] = this.orderBy;
    return state;
  }

  private restoreFilters(params: Params): void {
    if (!params || !params['page']) return;
    this.page = parseInt(params['page'], 10) || 1;
    // The platform/property dropdowns bind the whole option object, so rebuild
    // one rather than assigning the bare id (which would render blank).
    this.filterPlatform = params['platform']
      ? ((this.platformItems as any[]).find((i) => i.value === params['platform']) ?? null)
      : null;
    this.filterProperty = params['property_id']
      ? ({ value: params['property_id'], label: params['property_name'] ?? '' } as any)
      : null;
    this.filterStatus = params['status'] ?? null;
    this.filterSearch = params['search'] ?? '';
    this.sortBy = params['sortBy'] || this.sortBy;
    this.orderBy = params['orderBy'] || this.orderBy;
  }

  private unwrapFilter(value: any): string | null {
    if (!value) return null;
    return typeof value === 'object' ? value.value : value;
  }

  private labelOfFilter(value: any): string {
    return value && typeof value === 'object' ? (value.label ?? '') : '';
  }

  initUpdateForm(): void {
    this.updateForm = this.fb.group({
      follow_up_date: [''],
      status: ['']
    });
  }

  /**
   * Fetch enquiries including platform, property & status filters
   */
  getAllEnquiries() {
    let filters = '';
    // unwrap platform value if an object, otherwise use string
    const pVal = this.filterPlatform && typeof this.filterPlatform === 'object'
      ? (this.filterPlatform as any).value
      : this.filterPlatform;
    if (pVal) {
      filters += `&platform=${pVal}`;
    }
    // Use propertyId input if provided (from parent), otherwise use filter
    const propVal = this.propertyId
      ? this.propertyId
      : (this.filterProperty && typeof this.filterProperty === 'object'
          ? (this.filterProperty as any).value
          : this.filterProperty);
    if (propVal) {
      filters += `&property_id=${propVal}`;
    }
    // status filter - now receives direct value from ng-select
    if (this.filterStatus) {
      filters += `&status=${this.filterStatus}`;
    }
    // include free-text search
    if (this.filterSearch) {
      filters += `&search=${encodeURIComponent(this.filterSearch)}`;
    }
    this.getAllEnquiriesSubscription = this.httpService
      .getAllEnquiries(
        this.page,
        this.totalRows,
        this.sortBy,
        this.orderBy,
        filters
      )
      .subscribe({
        next: (response) => {
          this.orderData = response.data['enquiries'] ?? [];
          this.allData = this.paginator(
            this.orderData,
            this.page,
            this.totalRows,
            response.data?.total
          );
          this.totalPage = response.data.total;
          this.pageCount = response.data.pageCount;
        }
      });
  }

  sortData(sort: Sort) {
    if (!sort.active || sort.direction === '') {
      return;
    }
    this.sortBy = sort.active;
    this.orderBy = sort.direction.toUpperCase();
    this.getAllEnquiries();
  }

  pageChange(e: number) {
    if (this.page === e) return;
    this.page = e;
    this.getAllEnquiries();
  }

  viewEnquiry(id: string) {
    this.router.navigate(['admin', 'enquiries', 'view', id], {
      queryParams: this.currentFilterState()
    });
  }

  viewProperty(propertyId: string) {
    this.router.navigate(['admin', 'properties', 'property-view', propertyId]);
  }

  paginator(items: any, current_page: number, per_page_items: number, total_records: number) {
    let page = current_page || 1,
      per_page = per_page_items || 50,
      offset = (page - 1) * per_page,
      paginatedItems = items,
      total_pages = Math.ceil(items.length / per_page);

    return {
      page: page,
      startPage: offset + 1,
      endPage: page * per_page,
      per_page: per_page,
      pre_page: page - 1 ? page - 1 : null,
      next_page: (total_pages > page) ? page + 1 : null,
      total: total_records,
      total_pages: total_pages,
      data: paginatedItems
    };
  }

  get checkEnquiryPage() {
    return this.authService.checkPermission(USER_PERMISSIONS.ENQUIRIES.ENQUIRY_PAGE);
  }

  get checkEnquiryUpdate() {
    return this.authService.checkPermission(USER_PERMISSIONS.ENQUIRIES.ENQUIRY_UPDATE);
  }

  ngOnDestroy(): void {
    this.getAllEnquiriesSubscription?.unsubscribe();
    this.propertySearchSub?.unsubscribe();
    this.addFormPropertySearchSub?.unsubscribe();
  }

  /**
   * Configure property typeahead & lazy loader pipeline
   */
  private setupPropertySearch() {
    this.propertySearchSub = this.searchProperty$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          this.propertyPage = 1;
          this.hasMoreProperties = true;
        }),
        switchMap((term) => {
          const query = term ? `&name=${term}` : '';
          return this.httpService.getAllProperties(
            this.propertyPage,
            this.propertyPageSize,
            query
          );
        })
      )
      .subscribe((response) => {
        this.propertiesList = response.data?.properties ?? [];
        this.hasMoreProperties =
          this.propertiesList.length < (response.data?.total ?? 0);
        this.propertiesItems = this.propertiesList.map((p) => ({
          value: p.uid,
          label: p.name,
          data: { name: p.name },
        }));
      });
    // initial load
    this.searchProperty$.next('');
  }

  /**
   * Load next page of properties when scrolling dropdown
   */
  onPropertyScrollEnd() {
    if (!this.hasMoreProperties) return;
    this.propertyPage++;
    this.httpService
      .getAllProperties(this.propertyPage, this.propertyPageSize)
      .subscribe((resp) => {
        const more = resp.data?.properties ?? [];
        this.hasMoreProperties =
          this.propertiesList.length + more.length < (resp.data?.total ?? 0);
        this.propertiesList = [...this.propertiesList, ...more];
        this.propertiesItems = this.propertiesList.map((p) => ({
          value: p.uid,
          label: p.name,
          data: { name: p.name },
        }));
      });
  }

  /** Called when platform or property filter changes */
  onFilterChange() {
    this.page = 1;
    this.getAllEnquiries();
  }

  /** Reset filters */
  clearFilters() {
    this.filterPlatform = null;
    this.filterProperty = null;
    this.filterStatus = null;
    this.filterSearch = '';
    if (!this.propertyId) {
      this.router.navigate([], { relativeTo: this.route, queryParams: {} });
    }
    this.onFilterChange();
  }

  /** Open update modal */
  openUpdateModal(enquiry: any): void {
    this.selectedEnquiryId = enquiry.id;

    // Pre-populate form with existing values
    this.updateForm.patchValue({
      follow_up_date: enquiry.follow_up_date || '',
      status: enquiry.status || ''
    });

    // Open modal
    this.modalInstance = new bootstrap.Modal(this.updateModal.nativeElement);
    this.modalInstance.show();
  }

  /** Close update modal */
  closeUpdateModal(): void {
    this.modalInstance?.hide();
    this.updateForm.reset();
    this.selectedEnquiryId = '';
  }

  /** Submit update form */
  submitUpdate(): void {
    if (!this.selectedEnquiryId) return;

    const formData = this.updateForm.value;

    // Only send fields that have values
    const updateData: any = {};
    if (formData.follow_up_date) {
      updateData.follow_up_date = formData.follow_up_date;
    }
    if (formData.status) {
      updateData.status = formData.status;
    }

    // Don't make API call if no data to update
    if (Object.keys(updateData).length === 0) {
      this.alertService.error('Please select at least one field to update', 'toast-top-right');
      return;
    }

    this.loadingService.show();
    this.httpService.updateEnquiry(this.selectedEnquiryId, updateData).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.alertService.success(response.message || 'Enquiry updated successfully', 'toast-top-right');
          this.closeUpdateModal();
          this.getAllEnquiries(); // Refresh list
        } else {
          this.alertService.error(response.message || 'Error updating enquiry', 'toast-top-right');
        }
      },
      error: () => {
        this.loadingService.hide();
        // Error toast is handled by HTTP interceptor
      }
    });
  }

  // Add Enquiry Form Methods
  initAddEnquiryForm(): void {
    this.addEnquiryForm = this.fb.group({
      property_id: ['', Validators.required],
      property_type: [''],
      name: ['', Validators.required],
      phone: ['', Validators.required],
      email: ['', [Validators.email]],
      locality: [''],
      city: [''],
      state: [''],
      amount: [''],
      bath_room: [''],
      looking_for: [''],
      status: ['COLD'],
      follow_up_date: ['']
    });
  }

  setupAddFormPropertySearch(): void {
    this.addFormPropertySearchSub = this.searchAddFormProperty$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          this.addFormPropertyPage = 1;
          this.addFormHasMoreProperties = true;
        }),
        switchMap((term) => {
          const query = term ? `&name=${term}` : '';
          return this.httpService.getAllProperties(
            this.addFormPropertyPage,
            this.addFormPropertyPageSize,
            query
          );
        })
      )
      .subscribe((response) => {
        this.addFormPropertiesList = response.data?.properties ?? [];
        this.addFormHasMoreProperties =
          this.addFormPropertiesList.length < (response.data?.total ?? 0);
        this.addFormPropertiesItems = this.addFormPropertiesList.map((p) => ({
          value: p.uid,
          label: p.name,
          data: { name: p.name },
        }));
      });
    // Initial load
    this.searchAddFormProperty$.next('');
  }

  onAddFormPropertyScrollEnd(): void {
    if (!this.addFormHasMoreProperties) return;
    this.addFormPropertyPage++;
    this.httpService
      .getAllProperties(this.addFormPropertyPage, this.addFormPropertyPageSize)
      .subscribe((resp) => {
        const more = resp.data?.properties ?? [];
        this.addFormHasMoreProperties =
          this.addFormPropertiesList.length + more.length < (resp.data?.total ?? 0);
        this.addFormPropertiesList = [...this.addFormPropertiesList, ...more];
        this.addFormPropertiesItems = this.addFormPropertiesList.map((p) => ({
          value: p.uid,
          label: p.name,
          data: { name: p.name },
        }));
      });
  }

  get checkEnquiryAdd() {
    return this.authService.checkPermission(USER_PERMISSIONS.ENQUIRIES.ENQUIRY_ADD);
  }

  get checkEnquiryEdit() {
    return this.authService.checkPermission(USER_PERMISSIONS.ENQUIRIES.ENQUIRY_EDIT);
  }

  openAddEnquiryModal(): void {
    this.showAddModal = true;
    this.addEnquiryForm.reset();
    this.addEnquiryForm.patchValue({ status: 'COLD' });
  }

  closeAddEnquiryModal(): void {
    this.showAddModal = false;
    this.addEnquiryForm.reset();
  }

  submitAddEnquiry(): void {
    if (this.addEnquiryForm.valid) {
      const formData = this.addEnquiryForm.value;

      // Add platform as 'Direct'
      const enquiryData = {
        ...formData,
        platform: 'Direct'
      };

      // Remove empty fields
      Object.keys(enquiryData).forEach(key => {
        if (enquiryData[key] === '' || enquiryData[key] === null) {
          delete enquiryData[key];
        }
      });

      this.loadingService.show();
      this.httpService.createEnquiry(enquiryData).subscribe({
        next: (response: any) => {
          this.loadingService.hide();
          if (response.success) {
            this.alertService.success(response.message || 'Enquiry created successfully', 'toast-top-right');
            this.closeAddEnquiryModal();
            this.getAllEnquiries();
          } else {
            this.alertService.error(response.message || 'Error creating enquiry', 'toast-top-right');
          }
        },
        error: () => {
          this.loadingService.hide();
        }
      });
    } else {
      this.markFormGroupTouched(this.addEnquiryForm);
    }
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  isAddFieldInvalid(fieldName: string): boolean {
    const field = this.addEnquiryForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  editEnquiry(id: string): void {
    this.router.navigate(['admin', 'enquiries', 'edit', id], {
      queryParams: this.currentFilterState()
    });
  }

  canEditEnquiry(item: any): boolean {
    return this.checkEnquiryEdit && item.platform === 'Direct';
  }
}
